import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo, Suspense } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isNativeApp } from '../../utils/platform';
import {
  FiStar, FiMapPin, FiNavigation, FiMap, FiList,
  FiSearch, FiCalendar, FiCheckCircle, FiSliders, FiHeart, FiChevronDown, FiCheck,
} from 'react-icons/fi';
import { LuDog, LuCat, LuBird, LuRabbit, LuTurtle, LuPawPrint } from 'react-icons/lu';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, animate, useDragControls } from 'motion/react';
import useFavorites from '../../hooks/useFavorites';
import { mapApiSalon } from '../../utils/salonMapper';
import MarketplaceCard from '../../components/marketplace/MarketplaceCard';
import FilterModal from '../../components/marketplace/FilterModal';
import WhereModal from '../../components/marketplace/WhereModal';
import ExploreHero from '../../components/marketplace/ExploreHero';
import CategoryStrip from '../../components/marketplace/CategoryStrip';
import YourBookings from '../../components/marketplace/YourBookings';
import Button from '../../components/ui/Button';
import { clearFilterParams } from '../../components/marketplace/categories';
import { getUserLocation } from '../../utils/getUserLocation';
import { sortSalons, haversineKm } from '../../utils/salonRanking';
import { roPluralNoun } from '../../utils/i18n';
import Pagination from '../../components/common/Pagination';
import { SkeletonCard } from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import NewsletterPopup from '../../components/common/NewsletterPopup';
import api from '../../utils/api';
import lazyChunk from '../../utils/lazyChunk';

// The map view is code-split into SearchMapView.jsx so @react-google-maps/api
// (a heavy dependency) loads lazily only when the user opens the map — this
// keeps the eager HomePage bundle small, which matters on low-end devices.
const SearchMapView = lazyChunk(() => import('./SearchMapView'));

// Sort options surfaced to the user. The raw "highest rated" / "most reviews"
// options are deliberately gone: at low inventory they expose anomalies (one
// 5★ review topping fifty 4.8★ ones) and the platform's thin review volume.
// "Recomandate" (a composite relevance score — see utils/salonRanking.js) does
// that job more intelligently and is the default. Distance / price / newest
// give users the few self-evident axes that don't backfire at cold-start.
// All four sort CLIENT-SIDE; the list is fetched with a high limit (below).
const sortOptions = [
  { value: 'recomandate', label: 'Recomandate' },
  { value: 'distance', label: 'Aproape de tine' },
  { value: 'price', label: 'Preț crescător' },
  { value: 'newest', label: 'Cele mai noi' },
];

// Parse the ?page= query param as a 1-based page number. Hard public-boundary
// coercion: missing / non-numeric / <1 all fall back to 1.
function parsePageParam(params) {
  const n = parseInt(params.get('page'), 10);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

// HomePage IS the marketplace. The legacy /search page used to be a
// separate surface (curated landing vs. full results) but at Petzy
// scale the distinction created two pages with the same chrome and
// near-identical interaction model. They're merged here: the page
// fetches paginated results from the same /api/salons endpoint that
// the search route used to drive, the pill / strip / modals write
// to the same ?city= / ?category= / ?species= / ?q= URL params, and
// the marketing surface (Cum funcționează + B2B CTA + phone mockup)
// sits below the grid where landing visitors hit it after scrolling
// past the marketplace they came for.
//
// /search still routes here via a 301-style <Navigate replace>
// declared in App.jsx for backward-compat with old links.
export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [whereOpen, setWhereOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salons, setSalons] = useState([]);
  // Distinguish a load failure from a genuinely empty result — a network error
  // must read as "couldn't load, retry", not "no salons found" (misleading on a
  // flaky network, and an App Store 4.2 risk).
  const [error, setError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Wide screens (lg+) get the split list+map view — the Fresha layout (cards
  // left, map right). This is now purely WIDTH-driven, not web-only: the native
  // app on a wide window (iPad landscape, iOS-app-on-Mac) has room for the split
  // too, and its BottomNav "Caută" tab is `md:hidden` there — so gating the map
  // on `!isNativeApp` used to leave wide native with NO way to reach the map at
  // all. Narrow screens (phones, web or native) stay on the list; native's
  // narrow map entry is still the BottomNav "Caută" tab. matchMedia reacts to
  // resize (relevant on Mac/iPad where the window/orientation can change).
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    // addEventListener with the legacy addListener fallback — mirrors the
    // matchMedia hooks in LandingPage / SalonDashboard so an old MediaQueryList
    // (no addEventListener) can't TypeError-crash the page. Init stays
    // synchronous above: no SSR here (createRoot), so the layout is correct on
    // first paint with no false→true flash.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  // Drives the list<->map cross-dissolve below (disabled for reduced-motion).
  const reduced = useReducedMotion();
  // Grid vs map is URL-driven (?view=) so the bottom-nav "Caută" tab opens the
  // map directly and the active tab stays in sync. The page DEFAULTS to the LIST
  // (the full editorial hero is the landing); the map is the explicit ?view=map
  // toggle, where the hero collapses into a slim toolbar.
  const viewParam = searchParams.get('view');
  const viewMode = viewParam === 'map' ? 'map' : 'grid';
  const setViewMode = (mode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      // Set explicitly (don't delete): on desktop the no-param default is 'map',
      // so deleting 'grid' would bounce it straight back to the split.
      next.set('view', mode);
      return next;
    }, { replace: true });
  };
  // 'recomandate' (composite relevance) is the default — see sortOptions.
  const [sortBy, setSortBy] = useState('recomandate');
  // The grid's own geolocation fix, used by the 'recomandate' proximity term
  // and the 'distance' sort. Stays null (no prompt) until the user picks the
  // "Aproape de tine" sort — then we request it lazily; relevance degrades
  // gracefully without it. The map view keeps its own fix (SearchMapView).
  const [userPos, setUserPos] = useState(null);
  // Reorders the grid client-side; selecting "distance" lazily asks for a fix
  // and reverts to "recomandate" if the user denies it.
  const handleSortChange = (val) => {
    if (val === 'distance' && !userPos) {
      // Functional revert so a slow/failed geolocation only bounces back to
      // 'recomandate' if the user is STILL on 'distance' — otherwise a quick
      // switch to another sort (e.g. 'price') before the promise rejects would
      // get clobbered back to 'recomandate'.
      getUserLocation()
        .then(setUserPos)
        .catch(() => setSortBy((cur) => (cur === 'distance' ? 'recomandate' : cur)));
    }
    setSortBy(val);
  };

  // currentPage is URL-backed (?page=N). Clearing filters or changing a
  // category deletes `page` from the URL, so deriving it here snaps back to
  // page 1 instead of re-requesting a now-out-of-range page.
  const currentPage = parsePageParam(searchParams);
  // Accepts a plain page number or a functional updater (prev => next) so it
  // behaves like the useState setter it replaced. The previous page is read
  // from the URL inside the updater, keeping the callback stable. Page 1 is
  // the default, so it drops ?page= entirely.
  const setCurrentPage = useCallback((page) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolved = typeof page === 'function' ? page(parsePageParam(prev)) : page;
      if (resolved > 1) next.set('page', String(resolved));
      else next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  // city + tag are URL-backed — PillSearchBar / WhereModal / CategoryStrip /
  // FilterModal all commit them to the query string — so derive them straight
  // from searchParams. Single source of truth: no mirror state to drift, and a
  // modal-set city can't be clobbered by an unrelated URL change.
  // (Legacy `?category=` bookmarks no longer resolve — Phase 7 retired the
  // dual-read; canonical redirect would be a separate routing concern.)
  const selectedCity = searchParams.get('city') || '';
  const selectedTagSlug = searchParams.get('marketplaceTag') || '';
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [minRating, setMinRating] = useState(0);

  const queryText = searchParams.get('q') || '';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    // Ordering is done CLIENT-SIDE now (the "Recomandate" composite + distance +
    // price + newest — see utils/salonRanking.js), so we don't pass sortBy and
    // we fetch the whole filtered set in one page (high limit) instead of 12.
    // At cold-start a city has well under 100 salons; when inventory outgrows a
    // page, the non-distance part of the score moves server-side. Server-side
    // filtering (city/tag/price/rating/query/species) stays in the WHERE below.
    const params = {
      page: 1,
      limit: 100,
    };
    if (queryText) params.query = queryText;
    if (selectedCity) params.city = selectedCity;
    if (selectedTagSlug) params.marketplaceTag = selectedTagSlug;
    const speciesParam = searchParams.get('species');
    if (speciesParam) params.species = speciesParam;
    if (minRating > 0) params.rating = minRating;
    if (priceRange[0] > 0) params.minPrice = priceRange[0];
    if (priceRange[1] < 500) params.maxPrice = priceRange[1];

    (async () => {
      try {
        const res = await api.get('/salons', { params });
        if (cancelled) return;
        const apiSalons = (res.data?.salons || []).map(mapApiSalon);
        setSalons(apiSalons);
        setTotalPages(Number(res.data?.pagination?.totalPages || 1));
        setTotalCount(Number(res.data?.pagination?.total ?? apiSalons.length));
      } catch (err) {
        console.error('[HomePage] load error', err);
        if (!cancelled) {
          setError(true);
          setSalons([]);
          setTotalPages(1);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // searchParams.get('species') (NOT the whole searchParams object) so flipping
    // ?species= (the Pisici chip) refires the fetch — but toggling non-filter
    // params like ?view=map (list↔map) does NOT, which a whole-object dep would
    // wrongly refetch on. Every other filter (city/tag/price/rating/query) is its
    // own derived dep already; species is the only param read raw in the effect.
    // sortBy / currentPage are NOT deps: ordering is client-side (no refetch on
    // sort change) and we always fetch the single high-limit page.
  }, [selectedCity, selectedTagSlug, priceRange[0], priceRange[1], minRating, queryText, searchParams.get('species'), retryNonce]);

  const clearFilters = () => {
    // city + marketplaceTag are derived from the URL, so deleting the params
    // clears them; only the still-local price / rating need explicit resets.
    setPriceRange([0, 500]);
    setMinRating(0);
    setSearchParams((prev) => clearFilterParams(prev));
  };

  const activeFilterCount =
    (selectedCity ? 1 : 0) +
    (selectedTagSlug ? 1 : 0) +
    (searchParams.get('species') ? 1 : 0) +
    (minRating > 0 ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < 500 ? 1 : 0);

  // Client-side ordering. Drives both the grid and (via the prop below) the
  // desktop split's left list + the map venue sheet, so every surface shares
  // one ranking.
  const sortedSalons = useMemo(
    () => sortSalons(salons, sortBy, userPos),
    [salons, sortBy, userPos],
  );

  return (
    <div className="flex w-full flex-col items-center">
      {/* Web only — an interstitial newsletter popup is intrusive in the native
          app (and redundant: native users are already signed in with an email). */}
      {!isNativeApp && <NewsletterPopup />}

      {/* Hero + content. On the desktop MAP view this whole block is viewport-
          locked (no page scroll; footer hidden in AppShell) and the hero
          collapses into a slim toolbar; otherwise it flows normally with the
          full editorial hero on top (the landing). */}
      <div className={`flex w-full flex-col ${isDesktop && viewMode === 'map' ? 'h-[calc(100vh-4rem)] overflow-hidden' : ''}`}>
        <ExploreHero
          collapsed={isDesktop && viewMode === 'map'}
          onOpenWhere={() => setWhereOpen(true)}
        >
          {/* Map-mode toolbar controls, rendered inline next to the (compact)
              search so the whole top is a single slim row. */}
          {isDesktop && viewMode === 'map' && (
            <>
              <button
                type="button"
                onClick={() => setFiltersOpen(true)}
                className="inline-flex h-12 shrink-0 items-center gap-2 rounded-full border border-ink/15 px-4 text-sm font-medium text-ink-600 transition-colors hover:bg-ink/5"
              >
                <FiSliders className="h-4 w-4" aria-hidden="true" />
                Filtre
                {activeFilterCount > 0 && (
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-semibold text-white">{activeFilterCount}</span>
                )}
              </button>
              {/* Single button back to the list (Fresha-style) — same size +
                  shape as Filtre, no chunky 2-segment toggle. */}
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className="inline-flex h-12 shrink-0 items-center gap-2 rounded-full border border-ink/15 px-4 text-sm font-medium text-ink-600 transition-colors hover:bg-ink/5"
              >
                <FiList className="h-4 w-4" aria-hidden="true" /> Listă
              </button>
              {/* Sortare — appearance-none pill + a custom chevron so it reads as
                  a button matching Filtre (no native rectangle look). */}
              <div className="relative hidden h-12 shrink-0 xl:block">
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  aria-label="Sortare"
                  className="focus-ring h-12 appearance-none rounded-full border border-ink/15 bg-cream-50 pl-4 pr-9 text-sm text-ink"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <FiChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" aria-hidden="true" />
              </div>
            </>
          )}
        </ExploreHero>

      {/* Returning-client shortcut (native): your next appointment / rebook.
          Renders nothing for guests + first-time users, so the home stays
          airy for them. */}
      {isNativeApp && <YourBookings />}

      {/* Category pills — visible in list mode, animated OUT (fade + slide up +
          height collapse) when switching to the desktop map, in step with the
          hero text. Categories still live in the Filtre modal there. */}
      <AnimatePresence initial={false}>
        {!(isDesktop && viewMode === 'map') && (
          <motion.div
            key="cat-strip"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, y: -20, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="mx-auto w-full max-w-7xl overflow-hidden px-4 pt-2 sm:px-6 lg:px-8"
          >
            <CategoryStrip />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Results header — count + filters + sort. Hidden on the desktop map
          view: those controls move into the split's left column (Fresha). */}
      {!(isDesktop && viewMode === 'map') && (
      <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-3 px-4 pb-4 pt-6 sm:px-6 lg:px-8">
        <p className="text-sm text-ink-500">
          <span className="font-semibold text-ink-600">{totalCount}</span>{' '}
          {roPluralNoun(totalCount, 'salon găsit', 'saloane găsite')}
        </p>
        <div className="flex items-center gap-3">
          {/* Filtre — moved out of the search bar to sit with the other
              result controls; a labelled button + active-count badge,
              clearer than the bare sliders icon it replaces. */}
          {/* All controls share one height (h-11) + pill shape, like the map
              toolbar — consistent "weight" across the row. */}
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="inline-flex h-11 items-center gap-2 rounded-full border border-ink/15 px-4 text-sm font-medium text-ink-600 transition-colors hover:bg-ink/5"
          >
            <FiSliders className="h-4 w-4" aria-hidden="true" />
            Filtre
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-semibold text-white">
                {activeFilterCount}
                <span className="sr-only">{activeFilterCount === 1 ? ' filtru activ' : ' filtre active'}</span>
              </span>
            )}
          </button>
          {/* Single button → map (Fresha-style; same size + shape as Filtre).
              Shown on web at any width, AND on wide native (isDesktop) where the
              BottomNav "Caută" tab is hidden — so wide native can still reach the
              map. Dropped only on NARROW native, where the "Caută" tab owns it. */}
          {(!isNativeApp || isDesktop) && salons.length > 0 && (
            <button
              type="button"
              onClick={() => setViewMode('map')}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-ink/15 px-4 text-sm font-medium text-ink-600 transition-colors hover:bg-ink/5"
            >
              <FiMap className="h-4 w-4" aria-hidden="true" /> Hartă
            </button>
          )}
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="text-sm text-ink-500 transition-colors hover:text-ink-600"
            >
              Șterge filtrele
            </button>
          )}
          {/* Sortare — appearance-none pill + custom chevron (button look, no free
              "Sortare:" label, no native rectangle). */}
          <div className="relative hidden h-11 sm:block">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              aria-label="Sortare"
              className="focus-ring h-11 appearance-none rounded-full border border-ink/15 bg-cream-50 pl-4 pr-9 text-sm text-ink"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <FiChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-500" aria-hidden="true" />
          </div>
        </div>
      </div>
      )}

      {/* Marketplace grid (or the map view when toggled). The map split is BOXED
          in the same max-w-7xl container as the header (so the card edges align
          vertically with the logo + menu), fills the viewport-locked area
          (flex-1), and gets a little bottom padding so the map reads as a card. */}
      {/* List <-> map content swaps with a cross-dissolve + subtle zoom (Framer,
          mode="wait" so the two very different layouts never overlap; the brief
          fade-through is masked by the hero collapsing at the same time). */}
      <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={viewMode}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        className={viewMode === 'map'
        ? (isDesktop
            ? 'mx-auto flex w-full min-h-0 max-w-7xl flex-1 flex-col px-4 pb-4 sm:px-6 lg:px-8'
            : 'flex h-[calc(100vh-4rem)] w-full flex-col')
        : 'mx-auto w-full max-w-7xl px-4 pb-12 sm:px-6 lg:px-8'}>
        {viewMode === 'map' ? (
          <Suspense fallback={<div className="flex flex-1 items-center justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" /></div>}>
          <SearchMapView
            // Width-driven state that also picked the split container above —
            // the child must flip its internal layout from the SAME source,
            // otherwise wide native still rendered the fixed fullscreen map
            // inside the split shell (the #1616 gap flagged by review).
            isDesktop={isDesktop}
            salons={sortedSalons}
            onClose={() => setViewMode('grid')}
            isFavorite={isFavorite}
            onToggleFavorite={toggleFavorite}
            onOpenSearch={() => setWhereOpen(true)}
            onOpenFilters={() => setFiltersOpen(true)}
            activeFilterCount={activeFilterCount}
            sortBy={sortBy}
            sortOptions={sortOptions}
            onSortChange={handleSortChange}
            activeCity={searchParams.get('q') || searchParams.get('city') || ''}
            searchLabel={searchParams.get('q') || searchParams.get('city') || 'Locația curentă'}
            species={searchParams.get('species') || ''}
            onSpeciesChange={(val) => setSearchParams((prev) => {
              const n = new URLSearchParams(prev);
              if (val) n.set('species', val); else n.delete('species');
              n.delete('page');
              return n;
            }, { replace: true })}
          />
          </Suspense>
        ) : loading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-10">
            {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <EmptyState
            icon="📡"
            title="Nu am putut încărca saloanele"
            message="Verifică conexiunea la internet și încearcă din nou."
            action={() => setRetryNonce((n) => n + 1)}
            actionLabel="Reîncearcă"
          />
        ) : salons.length === 0 ? (
          <EmptyState
            icon="🔍"
            title="Niciun salon de toaletaj găsit"
            message="Încearcă să modifici filtrele sau să cauți altceva."
            action={clearFilters}
            actionLabel="Șterge filtrele"
          />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 lg:grid-cols-3 lg:gap-x-6 lg:gap-y-10">
              {sortedSalons.map((salon) => (
                <MarketplaceCard
                  key={salon.id}
                  salon={salon}
                  isFavorite={isFavorite(salon.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>

            <div className="mt-10">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          </>
        )}
      </motion.div>
      </AnimatePresence>
      </div>{/* /viewport-lock wrapper */}

      {/* ── Below-the-fold marketing — onboarding context for first-
          time visitors who scroll past the marketplace. Hidden in map
          mode so the map fills the viewport without competing copy
          stacked underneath it; hidden on native (the app home drops the
          "Cum funcționează" + B2B web-landing content). ──────────── */}

      {viewMode === 'grid' && !isNativeApp && (
        <>
      <section className="w-full bg-cream-50 py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Cum funcționează</h2>
            <p className="mx-auto mt-2 max-w-xl text-ink-500">
              Trei pași simpli pentru programarea perfectă la toaletaj
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              { icon: <FiSearch className="text-3xl" />, tile: 'bg-pastel-peach text-pastel-peach-deep', title: 'Caută', desc: 'Explorează saloanele de toaletaj din zona ta și compară serviciile, prețurile și recenziile.' },
              { icon: <FiCheckCircle className="text-3xl" />, tile: 'bg-pastel-sage text-pastel-sage-deep', title: 'Alege', desc: 'Selectează serviciul, groomerul și ora ideală pentru vizită.' },
              { icon: <FiCalendar className="text-3xl" />, tile: 'bg-pastel-sky text-pastel-sky-deep', title: 'Programează', desc: 'Confirmă programarea și primești confirmare instant. Simplu și rapid!' },
            ].map((step, idx) => (
              <div key={idx} className="relative text-center">
                <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl transition-transform duration-300 hover:scale-105 ${step.tile}`}>
                  {step.icon}
                </div>
                <span className="mb-3 inline-block h-6 w-6 rounded-full bg-ink text-xs font-bold leading-6 text-cream-50">
                  {idx + 1}
                </span>
                <h3 className="mb-2 font-display text-xl font-semibold text-ink">{step.title}</h3>
                <p className="leading-relaxed text-ink-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B owner-acquisition block — WEB ONLY. The native app is 100% B2C
          (App Store 3.1.1 anti-steering + 4.2 minimum functionality), so this
          "register your salon" surface isn't shipped in it. */}
      {!isNativeApp && (
      <section className="w-full overflow-hidden bg-ink py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">
            <div className="flex-1 text-center lg:text-left">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary-500/10 px-4 py-1.5">
                <span className="h-2 w-2 animate-pulse rounded-full bg-primary-500" />
                <span className="text-sm font-medium text-primary-300">Pentru saloane</span>
              </div>
              <h2 className="mb-6 font-display text-3xl font-bold text-cream-50 sm:text-4xl">
                Ai un salon de toaletaj?<br />Alătură-te platformei!
              </h2>
              <p className="mb-8 max-w-lg text-lg text-cream-50/70">
                Înregistrează-ți salonul pe Petzy și ajunge la mii de clienți noi. Gestionează programări, groomeri și servicii dintr-un singur loc.
              </p>
              <div className="flex flex-col items-center gap-4 sm:flex-row lg:items-start">
                <Button as={Link} to="/pro/register" variant="brand" size="lg">
                  Înregistrează salonul
                </Button>
                <Button as={Link} to="/pro" variant="ghostCream" size="lg">
                  Află mai multe
                </Button>
              </div>
            </div>

            <div className="relative mx-auto w-[280px] flex-shrink-0 sm:w-[300px]">
              {/* Ambient warm glow — separates the phone from bg-ink. */}
              <div className="absolute -inset-10 -z-10 rounded-[3rem] bg-primary-500/15 blur-3xl" aria-hidden="true" />
              {/* A REAL Petzy salon profile (Bubble Pet) — the same shot /pro uses.
                  This was ~235 lines of hand-coded dashboard mock ("Atelier Canin"
                  + invented appointments). Swapped for a genuine screenshot so a
                  salon owner sees a real listing on the platform, not a drawing. */}
              <img
                src="/devices/iphone-salon.webp"
                alt="Un profil real de salon pe Petzy, văzut pe iPhone"
                className="relative block h-auto w-full select-none pointer-events-none"
                draggable={false}
                style={{ filter: 'drop-shadow(16px 24px 28px rgba(0,0,0,0.45))' }}
              />
            </div>
          </div>
        </div>
      </section>
      )}
        </>
      )}

      {/* Modals & floating action button — same wiring SearchPage
          used. */}
      <WhereModal open={whereOpen} onClose={() => setWhereOpen(false)} />

      <FilterModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        minRating={minRating}
        setMinRating={setMinRating}
      />

    </div>
  );
}

